import React, { useEffect, useRef, useState } from "react";
import { Form, Select, Input, Checkbox, Image, Spin } from "antd";
import { useSearchParams } from "react-router-dom";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import { GetAllCountryApi } from "../../api/request/common.js";
import { RegisterApi, ResendSignupOtpApi, VerifySignupOtpApi } from "../../api/request/users.js";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useResponsive } from "../../hooks/useResponsive.js";
import YoutubeIcon from "../../assets/YoutubeIcon.jsx";

const fieldClass = "border border-[#E2E8F0] rounded-[10px] w-full ps-3 py-2.5 text-[15px]";
const PENDING_EMAIL_KEY = "signupPendingEmail";
const OTP_EXPIRES_KEY = "signupOtpExpiresAt";
const OTP_WAIT_SECONDS = 120;

function remainingOtpSeconds() {
  const expiresAt = Number(sessionStorage.getItem(OTP_EXPIRES_KEY));
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}

function rememberOtpExpiry() {
  sessionStorage.setItem(OTP_EXPIRES_KEY, String(Date.now() + OTP_WAIT_SECONDS * 1000));
}

function maskEmail(email) {
  if (!email || !email.includes("@")) return email;
  const [name, domain] = email.split("@");
  const visible = name.slice(0, 1);
  return `${visible}${"*".repeat(Math.max(name.length - 1, 1))}@${domain}`;
}

function formatTime(timer) {
  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function Register() {
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const resendLock = useRef(false);
  const [countries, setCountries] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [step, setStep] = useState("details");
  const [pendingEmail, setPendingEmail] = useState("");
  const [timer, setTimer] = useState(0);
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    countryId: null,
    password: "",
    confirmPassword: "",
    referralCode: (searchParams?.get("ref"))?.trim(),
  });

  const passwordRules = [
    { required: true, message: "Please enter a password." },
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();
        if (value.length < 8) return Promise.reject(new Error("Password must be at least 8 characters long."));
        if (!/[a-z]/.test(value)) return Promise.reject(new Error("Password must contain at least one lowercase letter."));
        if (!/[A-Z]/.test(value)) return Promise.reject(new Error("Password must contain at least one uppercase letter."));
        if (!/\d/.test(value)) return Promise.reject(new Error("Password must contain at least one number."));
        if (!/[^A-Za-z0-9]/.test(value)) return Promise.reject(new Error("Password must contain at least one special character."));
        return Promise.resolve();
      },
    },
  ];

  const getContriens = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(data?.data?.map((item) => ({ ...item, value: item?.id, label: item?.countryName })));
      }
    } catch (error) { }
  };

  useEffect(() => {
    getContriens();
    const savedEmail = sessionStorage.getItem(PENDING_EMAIL_KEY);
    if (savedEmail) {
      setPendingEmail(savedEmail);
      setStep("otp");
      setTimer(remainingOtpSeconds());
    }
  }, []);

  useEffect(() => {
    if (step !== "otp") return undefined;
    const tick = () => setTimer(remainingOtpSeconds());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [step]);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      const { data } = await RegisterApi(values);
      if (data?.success) {
        toast.success(data?.message);
        sessionStorage.setItem(PENDING_EMAIL_KEY, values.email);
        rememberOtpExpiry();
        setPendingEmail(values.email);
        setStep("otp");
        setTimer(OTP_WAIT_SECONDS);
        otpForm.resetFields();
      }
      setIsLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to create account.");
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (values) => {
    try {
      setIsLoading(true);
      const { data } = await VerifySignupOtpApi({
        email: pendingEmail,
        otp: Number(values.otp),
      });
      if (data?.success) {
        sessionStorage.removeItem(PENDING_EMAIL_KEY);
        sessionStorage.removeItem(OTP_EXPIRES_KEY);
        toast.success(data?.message);
        form.resetFields();
        setSearchParams({ tab: "login" });
      }
      setIsLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "OTP verification failed.");
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendLock.current || timer > 0) return;
    resendLock.current = true;
    setIsResending(true);
    try {
      const { data } = await ResendSignupOtpApi({ email: pendingEmail });
      if (data?.success) {
        toast.success(data?.message);
        otpForm.resetFields();
        rememberOtpExpiry();
        setTimer(OTP_WAIT_SECONDS);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to resend OTP.");
    } finally {
      resendLock.current = false;
      setIsResending(false);
    }
  };

  const editDetails = () => {
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
    sessionStorage.removeItem(OTP_EXPIRES_KEY);
    setStep("details");
    setTimer(0);
  };

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const isAllowKey = (e) => {
    const value = e.target.value;
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "End", "Home", "Enter"];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
    if (/[0-9]/.test(e.key) && value.length >= 15) {
      e.preventDefault();
    }
  };

  return (
    <div className="auth-page signup-form">
      <div className={`mb-5 ${step === "otp" ? "text-center" : ""}`}>
        <div className="flex items-center gap-2 mb-3">
          <span className={`h-1.5 flex-1 rounded-full ${step === "details" ? "bg-primary" : "bg-[#d7e6e8]"}`} />
          <span className={`h-1.5 flex-1 rounded-full ${step === "otp" ? "bg-primary" : "bg-[#d7e6e8]"}`} />
        </div>
        <p className="text-xl sm:text-[22px] leading-7 font-semibold text-[#1F2937]">
          {step === "details" ? "Create your account" : "Verify your email"}
        </p>
        <p className="text-sm text-[#6B7280] mt-1 break-words">
          {step === "details"
            ? "Open a Capital FX Global trading account in a few steps."
            : `Enter the 6-digit OTP sent to ${maskEmail(pendingEmail)}.`}
        </p>
      </div>

      {step === "details" ? (
        <Form autoComplete="off" form={form} initialValues={initialValues} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-x-3 gap-y-3">
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">First name</p>
              <Form.Item name="firstName" rules={[{ required: true, message: "Please enter first name." }]}>
                <Input placeholder="John" className={fieldClass} />
              </Form.Item>
            </div>
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Last name</p>
              <Form.Item name="lastName" rules={[{ required: true, message: "Please enter last name." }]}>
                <Input placeholder="Smith" className={fieldClass} />
              </Form.Item>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Email</p>
              <Form.Item name="email" rules={[{ required: true, type: "email", message: "Please enter a valid email." }]}>
                <Input placeholder="name@email.com" className={fieldClass} />
              </Form.Item>
            </div>
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Password</p>
              <Form.Item name="password" rules={passwordRules}>
                <Input.Password placeholder="Create a password" className="border border-[#E2E8F0] rounded-[10px] w-full ps-3 text-[15px]" />
              </Form.Item>
            </div>
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Confirm password</p>
              <Form.Item
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Password and confirm password do not match."));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter password" className="border border-[#E2E8F0] rounded-[10px] w-full ps-3 text-[15px]" />
              </Form.Item>
            </div>
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Phone number</p>
              <Form.Item name="phoneNo" rules={[{ required: true, message: "Please enter phone number." }]}>
                <Input maxLength={15} placeholder="Phone number" className={fieldClass} onKeyDown={(e) => isAllowKey(e)} />
              </Form.Item>
            </div>
            <div>
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Country</p>
              <Form.Item name="countryId" rules={[{ required: true, message: "Please select a country." }]}>
                <Select
                  showSearch
                  filterOption={filterOption}
                  options={countries}
                  placeholder="Select country"
                  className="w-full"
                  popupClassName="custom-select-dropdown"
                />
              </Form.Item>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[13px] text-[#374151] font-medium mb-1.5">Referral code <span className="text-[#9CA3AF] font-normal">(optional)</span></p>
              <Form.Item name="referralCode">
                <Input placeholder="Enter referral code" className={fieldClass} />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            name="conditions"
            valuePropName="checked"
            className="mt-4"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("Please accept the terms and conditions.")) }]}
          >
            <Checkbox className="signup-terms text-[#4B5563]">
              I agree to the
              <a href="/assets/NEPTUNE-TNC.pdf" download="NeptuneT&C" className="ms-1 primary font-medium hover:underline">
                Terms and Conditions
              </a>
            </Checkbox>
          </Form.Item>

          <div className="mt-4">
            <LoadableButton
              type="submit"
              className={`${isMobile ? "bg-primary-mobile rounded-[14px] py-3" : "bg-primary py-2.5"} text-white font-semibold text-base rounded-[10px] w-full`}
              lable="Open Account"
              isLoading={isLoading}
              loadingLable="Creating account..."
            />
          </div>

          <button
            type="button"
            className="w-full mt-3 border border-[#E7D5CC] text-[#9A5A42] bg-[#FFF8F5] font-medium text-sm sm:text-base px-4 py-2.5 rounded-[10px] flex justify-center items-center gap-2 text-center"
            onClick={() => setIsOpenModal(true)}
          >
            <YoutubeIcon />
            <span>Watch getting started tutorial</span>
          </button>

          <p className="text-center text-sm text-[#6B7280] mt-4">
            Already have an account? <Link to="/" className="primary font-semibold">Sign in</Link>
          </p>
        </Form>
      ) : (
        <Form form={otpForm} autoComplete="off" onFinish={handleVerifyOtp}>
          <div className="rounded-xl bg-[#F4FBFB] border border-[#D7EEEE] px-4 py-3 mb-5">
            <p className="text-xs uppercase tracking-wide text-[#00727D] font-semibold">OTP sent</p>
            <p className="text-sm text-[#374151] mt-1">Check your inbox and spam folder. The code expires shortly.</p>
          </div>
          <Form.Item
            name="otp"
            rules={[
              { required: true, message: "Please enter the OTP." },
              { len: 6, message: "OTP must be 6 digits." },
            ]}
          >
            <Input.OTP length={6} formatter={(value) => value.replace(/\D/g, "")} className="signup-otp" />
          </Form.Item>
          <div className="my-4 text-center text-sm text-[#4B5563]">
            {timer === 0 ? (
              <button
                type="button"
                onClick={resendOtp}
                disabled={isResending}
                className="primary font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <Spin size="small" />
                    Sending OTP...
                  </>
                ) : (
                  "Resend OTP"
                )}
              </button>
            ) : (
              <p>Resend OTP in {formatTime(timer)}</p>
            )}
          </div>
          <LoadableButton
            type="submit"
            className="bg-primary text-white font-semibold text-base rounded-[10px] w-full py-2.5"
            lable="Verify email"
            isLoading={isLoading}
            loadingLable="Verifying..."
          />
          <button type="button" onClick={editDetails} className="w-full text-sm text-[#6B7280] mt-4">
            Use a different email
          </button>
        </Form>
      )}

      {isOpenModal && (
        <Image
          style={{ display: "none" }}
          preview={{
            visible: isOpenModal,
            onVisibleChange: (value) => setIsOpenModal(value),
            destroyOnHidden: true,
            maskClassName: "!bg-black !bg-opacity-90",
            imageRender: () => (
              <div className="fixed inset-0 bg-opacity-60 flex items-center justify-center p-4">
                <div className="w-full h-full max-w-6xl flex items-center justify-center">
                  <div className="relative w-full h-full">
                    <iframe
                      src="https://www.youtube.com/embed/videoseries?list=PLQf-ZwtTMD1UMJVceEsEUIv7MrvH6qNCC&autoplay=1"
                      title="Process Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                </div>
              </div>
            ),
            toolbarRender: () => null,
          }}
        />
      )}
    </div>
  );
}

export default Register;
